import { useState, useMemo, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useAuth } from '../store/auth';
import { Btn } from '../components/Btn';
import { Ic } from '../components/Icon';
import { Input } from '../components/Field';
import { Logo } from '../components/Logo';
import { PasswordStrengthMeter, usePasswordRules } from '../components/PasswordStrengthMeter';
import type { HttpError } from '../api/client';

export function RegisterPage() {
  const setSession = useAuth((s) => s.setSession);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteFromUrl = params.get('invite') ?? '';

  const [inviteCode, setInviteCode] = useState(inviteFromUrl);
  const [email, setEmail] = useState('');
  const [nick, setNick] = useState('');
  const [password, setPassword] = useState('');

  const inviteInfo = useQuery({
    queryKey: ['invitation', inviteFromUrl],
    queryFn: () => authApi.invitationInfo(inviteFromUrl),
    enabled: !!inviteFromUrl,
  });

  // Prefill email if invitation has one
  useMemo(() => {
    if (inviteInfo.data?.email) setEmail(inviteInfo.data.email);
  }, [inviteInfo.data?.email]);

  const { allValid: passwordValid } = usePasswordRules(password);
  const allValid = passwordValid && nick.length >= 2 && !!email && !!inviteCode;

  const register = useMutation({
    mutationFn: () => authApi.register({ inviteCode, email, nick, password }),
    onSuccess: (data) => {
      if (data.requiresVerification || !data.token) return;
      // Auto-login post-register defaults to session-only — user can pick "stay
      // logged in" the next time they log in via the form with the checkbox.
      setSession(data.token, data.user, false);
      navigate('/');
    },
  });

  const pending = register.data?.requiresVerification === true;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (allValid) register.mutate();
  };

  return (
    <div
      className="auth-shell"
      style={{
        minHeight: '100vh',
        background: '#fbfaf6',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        color: '#0f1e3d',
        overflow: 'hidden',
      }}
    >
      <div
        className="desktop-only"
        style={{
          background: '#0f1e3d',
          color: '#fff',
          padding: '56px 56px',
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          position: 'relative',
        }}
      >
        <Logo size={80} textColor="#fff" subtitleColor="rgba(255,255,255,.6)" />
        <div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'rgba(244,185,58,.15)',
              color: 'var(--accent)',
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <Ic.mail size={12} />
            ZAŁÓŻ KONTO
          </div>
          <h1
            className="serif"
            style={{ fontSize: 44, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0 }}
          >
            Jeszcze tylko
            <br />
            chwilka.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,.68)', marginTop: 16 }}>
            Wpisz kod zaproszenia i swoje dane. Już prawie typujesz mundial.
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="auth-form"
        style={{
          padding: '56px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}>
        <div className="mobile-only" style={{ marginBottom: 24 }}>
          <Logo size={56} />
        </div>
        {pending ? (
          <PendingVerificationPanel email={register.data!.user.email} />
        ) : (<>
        <div style={{ fontSize: 12, color: '#5b6578', letterSpacing: '.08em', fontWeight: 600, textTransform: 'uppercase' }}>
          Załóż konto
        </div>
        <h2 className="serif" style={{ fontSize: 34, fontWeight: 700, margin: '6px 0 24px', letterSpacing: '-0.02em' }}>
          Twoje dane
        </h2>

        <Label>KOD ZAPROSZENIA</Label>
        <Input
          required
          value={inviteCode}
          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
          className="mono"
          placeholder="MORDOR-7X42"
          style={{ marginBottom: 14, letterSpacing: '.1em' }}
        />

        <Label>E-MAIL</Label>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ty@example.com"
          disabled={!!inviteInfo.data?.email}
          style={{ marginBottom: 14 }}
        />

        <Label>NICK W APLIKACJI</Label>
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <Input
            required
            value={nick}
            onChange={(e) => setNick(e.target.value.toLowerCase())}
            placeholder="ania.typuje"
            style={{ paddingLeft: 32 }}
          />
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#5b6578',
              fontSize: 14,
            }}
          >
            @
          </div>
        </div>

        <Label>HASŁO</Label>
        <Input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ fontFamily: 'Geist Mono, monospace', fontSize: 14 }}
        />

        <PasswordStrengthMeter password={password} />

        {register.isError && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background: 'rgba(208,70,60,.08)',
              color: '#8a2a22',
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            {(register.error as HttpError).body?.message ?? 'Rejestracja nie powiodła się'}
          </div>
        )}

        <Btn
          type="submit"
          variant="accent"
          size="lg"
          fullWidth
          disabled={!allValid || register.isPending}
          style={{ marginTop: 20 }}
        >
          {register.isPending ? 'Tworzenie konta…' : 'Utwórz konto'}
        </Btn>
        <div style={{ textAlign: 'center', fontSize: 12, color: '#5b6578', marginTop: 10 }}>
          Masz już konto?{' '}
          <Link to="/login" style={{ color: '#0f1e3d', fontWeight: 600 }}>
            Zaloguj się
          </Link>
        </div>
        </>)}
        </div>
      </form>
    </div>
  );
}

function PendingVerificationPanel({ email }: { email: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#5b6578', letterSpacing: '.08em', fontWeight: 600, textTransform: 'uppercase' }}>
        Sprawdź email
      </div>
      <h2 className="serif" style={{ fontSize: 34, fontWeight: 700, margin: '6px 0 16px', letterSpacing: '-0.02em' }}>
        Wysłaliśmy link aktywacyjny
      </h2>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: '#5b6578', marginBottom: 18 }}>
        Na adres <b style={{ color: '#0f1e3d' }}>{email}</b> poszedł mail z linkiem do aktywacji konta.
        Kliknij w niego, żeby się zalogować. Link wygasa za 24 godziny.
      </p>
      <div
        style={{
          padding: 14,
          background: '#f7f6f2',
          borderRadius: 10,
          border: '1px solid rgba(15,30,61,.05)',
          fontSize: 12.5,
          color: '#5b6578',
          lineHeight: 1.5,
        }}
      >
        Mail nie dotarł? Sprawdź spam lub{' '}
        <Link to="/login" style={{ color: '#0f1e3d', fontWeight: 600 }}>
          spróbuj wysłać go ponownie ze strony logowania
        </Link>
        .
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#5b6578',
  fontWeight: 600,
  letterSpacing: '.06em',
  marginBottom: 6,
};

function Label({ children }: { children: React.ReactNode }) {
  return <label style={labelStyle}>{children}</label>;
}
